'use client';
import React, { useState } from 'react';
import { X, Zap, AlertTriangle, Cpu, Settings2, ChevronDown, ChevronUp, Clock, TrendingUp, Server, Shield } from 'lucide-react';
import type { CanvasNode, CanvasEdge } from '@/lib/archsim-data';
import type { SimulationState, SimMetrics } from './ArchitectureCanvasScreen';
import { ALL_COMPONENTS, COMPONENT_GROUPS } from '@/lib/archsim-data';

interface NodeMonitorPopupProps {
  node: CanvasNode;
  simState: SimulationState;
  simMetrics: SimMetrics;
  allNodes?: CanvasNode[];
  allEdges?: CanvasEdge[];
  onClose: () => void;
  onOpenInspector: () => void;
  onUpdateConfig?: (id: string, config: Record<string, string | number | boolean>) => void;
  isLandscape?: boolean;
}

function statusColor(status: CanvasNode['status']) {
  switch (status) {
    case 'active': return 'bg-positive';
    case 'degraded': return 'bg-warning';
    case 'failed': return 'bg-danger';
    default: return 'bg-muted-foreground/40';
  }
}

function statusLabel(status: CanvasNode['status']) {
  switch (status) {
    case 'active': return 'Active';
    case 'degraded': return 'Degraded';
    case 'failed': return 'Failed';
    default: return 'Idle';
  }
}

// System-level param keys to hide behind collapsible
const SYSTEM_PARAM_KEYS = new Set([
  'serviceRatePerInstance', 'instances', 'cacheSizeGB', 'workingSetSizeGB',
  'alpha', 'rateLimit', 'burstLimit', 'timeoutMs', 'consumerCapacity',
  'partitions', 'circuitState', 'cacheRatio', 'instanceSize', 'readReplicas',
  'shards', 'maxConnections', 'replicationFactor', 'ttl', 'evictionPolicy',
  'connectionPoolSize', 'workerThreads', 'gcPauseMs', 'heapSizeGB',
]);

export default function NodeMonitorPopup({ node, simState, simMetrics, allNodes = [], allEdges = [], onClose, onOpenInspector, onUpdateConfig, isLandscape = false }: NodeMonitorPopupProps) {
  const [sysParamsOpen, setSysParamsOpen] = useState(false);

  const isActive = simState !== 'stopped';
  const def = ALL_COMPONENTS.find(c => c.id === node.defId);
  const m = node.metrics;

  const rps = isActive ? m.rps : 0;
  const latency = isActive ? m.latency : 0;
  const errorRate = isActive ? m.errorRate : 0;
  const cpuLoad = isActive ? m.cpuLoad : 0;

  // Determine if this is a client node
  const isClientNode = def?.group === 'Clients';

  // Compute per-client RPS and total input load across all client nodes
  const clientRps = isActive ? m.rps : 0;
  const totalInputLoad = isActive
    ? allNodes
        .filter(n => {
          const d = ALL_COMPONENTS.find(c => c.id === n.defId);
          return d?.group === 'Clients';
        })
        .reduce((sum, n) => sum + (n.metrics?.rps ?? 0), 0)
    : 0;

  // Current configured RPS for this client node
  const configuredRps = (node.config?.clientRps as number) ?? 1000;

  // Derived metrics
  const p99Latency = latency * 3;
  const throughputPct = def ? Math.min(100, (rps / def.defaultThroughput) * 100) : 0;
  const memLoad = isActive ? Math.min(99, cpuLoad * 0.85 + (errorRate > 0 ? 5 : 0)) : 0;
  const queueDepth = isActive && rps > 0 ? Math.max(0, (rps * latency) / 1000) : 0;

  const latencyColor = latency > 300 ? 'text-danger' : latency > 100 ? 'text-warning' : isActive ? 'text-positive' : 'text-muted-foreground';
  const errorColor = errorRate > 5 ? 'text-danger' : errorRate > 1 ? 'text-warning' : isActive ? 'text-positive' : 'text-muted-foreground';
  const cpuColor = cpuLoad > 90 ? 'text-danger' : cpuLoad > 70 ? 'text-warning' : isActive ? 'text-positive' : 'text-muted-foreground';
  const memColor = memLoad > 90 ? 'text-danger' : memLoad > 70 ? 'text-warning' : isActive ? 'text-positive' : 'text-muted-foreground';
  const p99Color = p99Latency > 300 ? 'text-danger' : p99Latency > 100 ? 'text-warning' : isActive ? 'text-positive' : 'text-muted-foreground';

  // Split config into essential vs system params
  const allConfigEntries = Object.entries(node.config ?? {}).filter(([, v]) => v !== '' && v !== undefined && v !== null);
  const essentialEntries = allConfigEntries.filter(([k]) => !SYSTEM_PARAM_KEYS.has(k));
  const systemEntries = allConfigEntries.filter(([k]) => SYSTEM_PARAM_KEYS.has(k));

  // Node group color
  const groupColor = def ? (COMPONENT_GROUPS[def.group]?.color ?? '#60a5fa') : '#60a5fa';

  // Landscape: compact game-style popup
  const containerStyle: React.CSSProperties = isLandscape
    ? { width: 'min(260px, calc(50vw - 16px))', maxHeight: 'min(260px, calc(100dvh - 80px))' }
    : { width: 'min(380px, calc(100vw - 24px))', maxHeight: 'min(480px, calc(100dvh - 112px))' };

  return (
    <div
      className={`bg-card border border-border/80 rounded-lg shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150 flex flex-col ${isLandscape ? 'game-border' : ''}`}
      style={containerStyle}
    >
      {/* Header */}
      <div className={`flex items-center gap-2 border-b border-border bg-muted/30 shrink-0 ${isLandscape ? 'px-2 py-1.5' : 'px-3 py-2.5'}`}>
        <div
          className={`rounded-md flex items-center justify-center shrink-0 ${isLandscape ? 'w-5 h-5 text-xs' : 'w-6 h-6 text-sm'}`}
          style={{ backgroundColor: groupColor + '22', border: `1px solid ${groupColor}44` }}
        >
          <span>{def?.icon ?? '⚙️'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-foreground truncate leading-tight ${isLandscape ? 'text-[10px]' : 'text-xs'}`}>{node.label}</p>
          {!isLandscape && <p className="text-[10px] text-muted-foreground truncate leading-tight">{def?.name ?? node.defId} · {def?.group}</p>}
        </div>
        <span className={`font-medium px-1.5 py-0.5 rounded-full border shrink-0 ${isLandscape ? 'text-[8px]' : 'text-[10px]'} ${
          node.status === 'active' ? 'border-positive/40 text-positive bg-positive/10' :
          node.status === 'degraded' ? 'border-warning/40 text-warning bg-warning/10' :
          node.status === 'failed' ? 'border-danger/40 text-danger bg-danger/10' : 'border-border text-muted-foreground bg-muted/20'
        }`}>
          <span className={`inline-block rounded-full mr-1 align-middle ${isLandscape ? 'w-1 h-1' : 'w-1.5 h-1.5'} ${node.status === 'active' ? 'bg-positive animate-pulse' : node.status === 'degraded' ? 'bg-warning' : node.status === 'failed' ? 'bg-danger' : 'bg-muted-foreground/40'}`} />
          {node.status === 'active' ? 'OK' : node.status === 'degraded' ? 'DEG' : node.status === 'failed' ? 'FAIL' : 'IDLE'}
        </span>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors ml-1 shrink-0 p-0.5 rounded hover:bg-muted/50"
          aria-label="Close"
        >
          <X size={isLandscape ? 11 : 14} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {/* Live metrics grid — compact in landscape */}
        <div className={`${isLandscape ? 'px-2 pt-1.5 pb-1' : 'px-3 pt-2.5 pb-1'}`}>
          <div className={`grid gap-1 ${isLandscape ? 'grid-cols-3' : 'grid-cols-3'}`}>
            {/* RPS */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <Zap size={7} className="text-info" />
                <span className="text-[7px] text-muted-foreground uppercase">RPS</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? 'text-info' : 'text-muted-foreground'}`}>
                {isActive ? (rps >= 1000 ? `${(rps / 1000).toFixed(1)}k` : rps.toFixed(0)) : '—'}
              </p>
            </div>
            {/* Latency */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <Clock size={7} className={isActive ? latencyColor : 'text-muted-foreground'} />
                <span className="text-[7px] text-muted-foreground uppercase">ms</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? latencyColor : 'text-muted-foreground'}`}>
                {isActive ? latency.toFixed(0) : '—'}
              </p>
            </div>
            {/* Error */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <AlertTriangle size={7} className={isActive ? errorColor : 'text-muted-foreground'} />
                <span className="text-[7px] text-muted-foreground uppercase">Err%</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? errorColor : 'text-muted-foreground'}`}>
                {isActive ? errorRate.toFixed(1) : '—'}
              </p>
            </div>
            {/* CPU */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <Cpu size={7} className={isActive ? cpuColor : 'text-muted-foreground'} />
                <span className="text-[7px] text-muted-foreground uppercase">CPU%</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? cpuColor : 'text-muted-foreground'}`}>
                {isActive ? cpuLoad.toFixed(0) : '—'}
              </p>
            </div>
            {/* P99 */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <TrendingUp size={7} className={isActive ? p99Color : 'text-muted-foreground'} />
                <span className="text-[7px] text-muted-foreground uppercase">P99</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? p99Color : 'text-muted-foreground'}`}>
                {isActive ? p99Latency.toFixed(0) : '—'}
              </p>
            </div>
            {/* Mem */}
            <div className="bg-muted/20 rounded px-1.5 py-1 border border-border/40">
              <div className="flex items-center gap-0.5 mb-0.5">
                <Server size={7} className={isActive ? memColor : 'text-muted-foreground'} />
                <span className="text-[7px] text-muted-foreground uppercase">Mem%</span>
              </div>
              <p className={`font-bold font-mono leading-none ${isLandscape ? 'text-[10px]' : 'text-sm'} ${isActive ? memColor : 'text-muted-foreground'}`}>
                {isActive ? memLoad.toFixed(0) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Client RPS slider — only in non-landscape or if space allows */}
        {isClientNode && !isLandscape && (
          <div className="px-3 pt-2 pb-1 border-t border-border/50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[8px] text-muted-foreground uppercase tracking-wide">Target RPS</span>
              <span className="text-xs font-mono font-bold text-info">{configuredRps.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min={100}
              max={50000}
              step={100}
              value={configuredRps}
              onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (onUpdateConfig) onUpdateConfig(node.id, { clientRps: val });
              }}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-info bg-muted/40"
            />
          </div>
        )}

        {/* Config — only in non-landscape */}
        {!isLandscape && essentialEntries.length > 0 && (
          <div className="px-3 py-2 border-t border-border">
            <div className="flex items-center gap-1 mb-1.5">
              <Settings2 size={9} className="text-muted-foreground" />
              <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">Config</span>
            </div>
            <div className="space-y-0.5">
              {essentialEntries.slice(0, 5).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-muted-foreground capitalize truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="text-[10px] font-mono text-foreground/80 shrink-0 max-w-[120px] truncate">{String(val)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System params — only in non-landscape */}
        {!isLandscape && systemEntries.length > 0 && (
          <div className="border-t border-border">
            <button
              onClick={() => setSysParamsOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 active:bg-muted/50 transition-colors touch-manipulation"
            >
              <div className="flex items-center gap-1.5">
                <Shield size={10} className="text-info/70" />
                <span className="text-[10px] text-foreground/70 font-medium">System Parameters</span>
                <span className="text-[9px] bg-muted/60 text-muted-foreground px-1 py-0.5 rounded-full ml-0.5">{systemEntries.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[9px] text-muted-foreground">{sysParamsOpen ? 'Hide' : 'Show'}</span>
                {sysParamsOpen ? <ChevronUp size={12} className="text-muted-foreground" /> : <ChevronDown size={12} className="text-muted-foreground" />}
              </div>
            </button>
            {sysParamsOpen && (
              <div className="px-3 pb-2.5 space-y-1 max-h-40 overflow-y-auto">
                {systemEntries.map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between gap-2 py-0.5">
                    <span className="text-[10px] text-muted-foreground capitalize truncate">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-[10px] font-mono text-foreground/70 shrink-0 max-w-[120px] truncate bg-muted/30 px-1 rounded">{String(val)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`border-t border-border bg-muted/20 shrink-0 ${isLandscape ? 'px-2 py-1' : 'px-3 py-2.5'}`}>
        <button
          onClick={() => { onOpenInspector(); onClose(); }}
          className={`w-full text-muted-foreground hover:text-foreground transition-colors text-center rounded hover:bg-muted/40 active:bg-muted/60 touch-manipulation ${isLandscape ? 'text-[9px] py-0.5' : 'text-[11px] py-1'}`}
        >
          {isLandscape ? 'Inspector →' : 'Open full inspector →'}
        </button>
      </div>
    </div>
  );
}
